import { createContext, useCallback, useContext, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, Download, ExternalLink } from 'lucide-react';

export interface GalleryImage {
  url: string;
  name: string;
}

/* ─────────────────────────────────────────────────────────────
   In-app image viewer. A page wraps its content in
   <ImageLightboxProvider images={...}> with every image the page shows, in
   display order; any descendant (inline message images, attachment chips)
   calls useImageLightbox()?.openImage(url) and the image opens in a pop-up
   overlay with ←/→ arrows through the whole set — no new tabs. Outside a
   provider the hook returns null and callers keep their link fallback.
───────────────────────────────────────────────────────────── */

interface LightboxApi {
  openImage: (url: string, name?: string) => void;
}

const LightboxContext = createContext<LightboxApi | null>(null);

export function useImageLightbox(): LightboxApi | null {
  return useContext(LightboxContext);
}

export function ImageLightboxProvider({ images, children }: { images: GalleryImage[]; children: ReactNode }) {
  // Open state: an index into `images`, or a one-off image that isn't in the
  // gallery (still viewable, just without arrows).
  const [index, setIndex] = useState<number | null>(null);
  const [loose, setLoose] = useState<GalleryImage | null>(null);

  const openImage = useCallback((url: string, name?: string) => {
    const i = images.findIndex(img => img.url === url);
    if (i >= 0) { setIndex(i); setLoose(null); }
    else { setLoose({ url, name: name || 'image' }); setIndex(null); }
  }, [images]);

  const close = useCallback(() => { setIndex(null); setLoose(null); }, []);

  const api = useMemo(() => ({ openImage }), [openImage]);

  const current = loose ?? (index !== null ? images[index] : null);
  const canBrowse = loose === null && images.length > 1;

  const step = useCallback((dir: 1 | -1) => {
    if (loose !== null || images.length < 2) return;
    setIndex(i => (i === null ? 0 : (i + dir + images.length) % images.length));
  }, [loose, images.length]);

  useEffect(() => {
    if (!current) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'ArrowLeft') step(-1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [current, close, step]);

  return (
    <LightboxContext.Provider value={api}>
      {children}
      {current && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Image viewer: ${current.name}`}
          onClick={close}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.87)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {/* Top bar: filename + actions */}
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position: 'absolute', top: 0, left: 0, right: 0,
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 14px',
              background: 'linear-gradient(rgba(0,0,0,0.55), transparent)',
            }}
          >
            <span style={{ color: '#fff', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {current.name}
            </span>
            {canBrowse && index !== null && (
              <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, flexShrink: 0 }}>
                {index + 1} / {images.length}
              </span>
            )}
            <span style={{ flex: 1 }} />
            <a
              href={current.url}
              target="_blank"
              rel="noreferrer"
              title="Open original in a new tab"
              style={lightboxBtnStyle}
            >
              <ExternalLink size={16} />
            </a>
            <a
              href={current.url.includes('?') ? `${current.url}&download=1` : `${current.url}?download=1`}
              title="Download"
              style={lightboxBtnStyle}
            >
              <Download size={16} />
            </a>
            <button type="button" onClick={close} title="Close (Esc)" style={lightboxBtnStyle}>
              <X size={18} />
            </button>
          </div>

          {canBrowse && (
            <button
              type="button"
              onClick={e => { e.stopPropagation(); step(-1); }}
              title="Previous image (←)"
              aria-label="Previous image"
              style={{ ...lightboxBtnStyle, position: 'absolute', left: 12, width: 42, height: 42 }}
            >
              <ChevronLeft size={26} />
            </button>
          )}

          <img
            src={current.url}
            alt={current.name}
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '92vw', maxHeight: '84vh', objectFit: 'contain', borderRadius: 6 }}
          />

          {canBrowse && (
            <button
              type="button"
              onClick={e => { e.stopPropagation(); step(1); }}
              title="Next image (→)"
              aria-label="Next image"
              style={{ ...lightboxBtnStyle, position: 'absolute', right: 12, width: 42, height: 42 }}
            >
              <ChevronRight size={26} />
            </button>
          )}
        </div>,
        document.body,
      )}
    </LightboxContext.Provider>
  );
}

const lightboxBtnStyle: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
  background: 'rgba(255,255,255,0.14)', color: '#fff',
  border: 'none', cursor: 'pointer', textDecoration: 'none',
};
